import React, { createContext, useContext, useState, useCallback } from 'react';
import EventModal from '../Modals/EventModal';
import BulkCreateEventsModal from '../Modals/BulkCreateEventsModal';
import ActivityModal from '../Modals/ActivityModal';
import MultiFlightModal from '../Modals/MultiFlightModal';
import TourCreationModal from '../components/TourCreationModal';
import { createTour, createEvent } from '../dataconnect-generated/js/default-connector';
import { resolvePlace, placeFromGoogle } from '../services/placeService';
import { useActiveArtists } from '../contexts/ArtistContext';
import useRefreshData from '../hooks/useRefreshData';

const QuickCreateContext = createContext(null);

export const QuickCreateProvider = ({ children }) => {
    const [showEventModal, setShowEventModal] = useState(false);
    const [showBulkCreateEventsModal, setShowBulkCreateEventsModal] = useState(false);
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [showTourModal, setShowTourModal] = useState(false);
    const [showFlightsModal, setShowFlightsModal] = useState(false);

    const [selectedDate, setSelectedDate] = useState(null);
    const [initialEvent, setInitialEvent] = useState(null);
    const [currentActivity, setCurrentActivity] = useState(null);

    const { data: activeArtists } = useActiveArtists();
    const { refreshData } = useRefreshData();

    const openCreateEvent = useCallback((date = null) => {
        setInitialEvent(null);
        if (date) setSelectedDate(date);
        setShowEventModal(true);
    }, []);

    const openCreateEvents = useCallback(() => {
        setShowBulkCreateEventsModal(true);
    }, []);

    const openCreateActivity = useCallback((date = null, activity = null) => {
        setSelectedDate(date || new Date().toISOString().split('T')[0]);
        setCurrentActivity(activity);
        setShowActivityModal(true);
    }, []);

    const openCreateTour = useCallback(() => {
        setShowTourModal(true);
    }, []);

    const openCreateFlights = useCallback(() => {
        setShowFlightsModal(true);
    }, []);

    const handleCreateTourSubmit = async (tourData) => {
        try {
            // Create the tour first (exclude the events array)
            const tourResult = await createTour({
                name: tourData.name,
                artistId: tourData.artistId,
                description: tourData.description,
                startDate: tourData.startDate ? new Date(tourData.startDate).toISOString().split('T')[0] : null,
                endDate: tourData.endDate ? new Date(tourData.endDate).toISOString().split('T')[0] : null,
                status: tourData.status
            });

            const createdTourId = tourResult.data.tour_insert.id;

            // Create locations and venues for each event
            if (tourData.events && tourData.events.length > 0) {
                const eventCreationPromises = tourData.events.map(async (event) => {
                    try {
                        let venueId = null;

                        // Create location if venue has location data
                        if (event.venueSelected?.geometry?.location) {
                            // Reuses the venue if this place is already in Tourminator
                            const resolved = await resolvePlace(placeFromGoogle(event.venueSelected, {
                                name: event.venueName || event.venueSelected.name,
                                city: event.location || null,
                                country: event.country || null,
                            }), { artistId: tourData.artistId, asVenue: true });
                            venueId = resolved.venueId;
                        }

                        // Create the event linked to the tour
                        const eventResult = await createEvent({
                            artistId: tourData.artistId,
                            name: event.venueName, // Use venue name as event name
                            date: new Date(event.date).toISOString().split('T')[0],
                            tourId: createdTourId,
                            venueId: venueId,
                            status: 'PLANNED',
                            type: 'CONCERT'
                        });

                        return eventResult;
                    } catch (error) {
                        console.error('Error creating event for tour:', error);
                        return null;
                    }
                });

                await Promise.all(eventCreationPromises);
            }

            setShowTourModal(false);

            if (tourData.artistId) {
                refreshData(tourData.artistId);
            } else {
                refreshData();
            }
        } catch (error) {
            console.error('Error creating tour from QuickCreateProvider:', error);
            throw error;
        }
    };

    return (
        <QuickCreateContext.Provider
            value={{
                openCreateEvent,
                openCreateEvents,
                openCreateActivity,
                openCreateTour,
                openCreateFlights,
            }}
        >
            {children}

            {showEventModal && (
                <EventModal
                    show={showEventModal}
                    handleClose={() => setShowEventModal(false)}
                    selectedDate={selectedDate}
                    initialEvent={initialEvent}
                />
            )}

            {showBulkCreateEventsModal && (
                <BulkCreateEventsModal
                    open={showBulkCreateEventsModal}
                    onClose={() => setShowBulkCreateEventsModal(false)}
                />
            )}

            {showActivityModal && (
                <ActivityModal
                    open={showActivityModal}
                    onClose={() => setShowActivityModal(false)}
                    selectedDate={selectedDate}
                    activityToEdit={currentActivity}
                />
            )}

            {showTourModal && (
                <TourCreationModal
                    open={showTourModal}
                    onClose={() => setShowTourModal(false)}
                    artist={activeArtists?.[0] || null}
                    onCreateTour={handleCreateTourSubmit}
                />
            )}

            <MultiFlightModal
                open={showFlightsModal}
                onClose={() => setShowFlightsModal(false)}
                saveAsDraft={false}
                onSuccess={(artistId) => {
                    setShowFlightsModal(false);
                    refreshData(artistId);
                }}
            />
        </QuickCreateContext.Provider>
    );
};

export const useQuickCreate = () => {
    const context = useContext(QuickCreateContext);
    if (!context) {
        throw new Error('useQuickCreate must be used within a QuickCreateProvider');
    }
    return context;
};
